import { Button, Card } from '@ohear/ui'
/**
 * Joyride-style overlay tour.
 *
 * Renders to `document.body` via a portal so the spotlight/tooltip sit above
 * the existing app chrome regardless of where `<Tour>` is mounted in the
 * tree. The spotlight is a CSS radial-gradient — no extra DOM elements get
 * grafted onto the highlighted target, which means existing layouts and
 * click handlers are not disturbed.
 *
 * Mechanics:
 *   - Resolves `step.selector` to a `DOMRect` each time the step changes,
 *     and re-resolves on `resize` + capturing `scroll` so the spotlight
 *     follows the element if the page rearranges underneath it.
 *   - When the anchor cannot be found, the tooltip renders centered with no
 *     spotlight (used for the "Welcome" step that has no selector).
 *   - Tooltip placement is auto-clamped to the viewport so it never falls
 *     off the edge on mobile.
 *   - Esc dismisses; clicking the dim backdrop does NOT (avoid accidental
 *     dismiss while reading).
 */
import type { CSSProperties, KeyboardEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { TourStep } from './TourStep'

export interface TourProps {
  steps: readonly TourStep[]
  /** Called when the user finishes the last step or clicks Skip. */
  onComplete: () => void
}

const TOOLTIP_MAX_WIDTH = 360
const TOOLTIP_GUTTER = 16

export function Tour({ steps, onComplete }: TourProps) {
  const [stepIdx, setStepIdx] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const step = steps[stepIdx]

  // Resolve and track the highlighted element's bounding box.
  useLayoutEffect(() => {
    if (!step?.selector) {
      setRect(null)
      return undefined
    }
    let frame = 0
    const measure = () => {
      const el = document.querySelector(step.selector!)
      if (!el) {
        setRect(null)
        return
      }
      setRect(el.getBoundingClientRect())
    }
    // Initial measure on the next frame so the page has settled.
    frame = requestAnimationFrame(measure)
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    // Use capture so we catch scroll events on inner scroll containers too.
    window.addEventListener('scroll', onResize, true)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [step])

  const advance = useCallback(() => {
    if (stepIdx + 1 >= steps.length) {
      onComplete()
      return
    }
    setStepIdx(stepIdx + 1)
  }, [stepIdx, steps.length, onComplete])

  const back = useCallback(() => {
    if (stepIdx === 0) return
    setStepIdx(stepIdx - 1)
  }, [stepIdx])

  // Esc dismisses. Bound at window so it works even if focus is elsewhere.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onComplete()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onComplete])

  if (!step) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-title-${step.id}`}
      aria-describedby={`tour-body-${step.id}`}
      style={overlayStyle(rect)}
      // Block clicks from reaching the page underneath, but absorb them
      // (don't dismiss on backdrop click — the user may be re-reading).
      onClick={absorb}
      onKeyDown={absorbKey}
    >
      <Card style={tooltipStyle(rect, step.placement)}>
        <h3
          id={`tour-title-${step.id}`}
          style={{ margin: '0 0 6px 0', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}
        >
          {step.title}
        </h3>
        <p
          id={`tour-body-${step.id}`}
          style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}
        >
          {step.body}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            gap: 8,
          }}
        >
          <span
            style={{ fontSize: 12, color: 'var(--text-3)' }}
            aria-label={`Step ${stepIdx + 1} of ${steps.length}`}
          >
            <span style={{ color: 'var(--text-2)' }}>{stepIdx + 1}</span>
            {' / '}
            {steps.length}
          </span>
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <Button variant="ghost" onClick={onComplete} aria-label="Skip tour">
              Skip
            </Button>
            {stepIdx > 0 ? (
              <Button variant="ghost" onClick={back}>
                Back
              </Button>
            ) : null}
            <Button variant="primary" onClick={advance}>
              {stepIdx + 1 < steps.length ? 'Next' : 'Done'}
            </Button>
          </div>
        </div>
      </Card>
    </div>,
    document.body,
  )
}

function absorb(e: { stopPropagation: () => void }) {
  e.stopPropagation()
}
function absorbKey(e: KeyboardEvent<HTMLDivElement>) {
  // Don't swallow Escape — the window-level handler dismisses.
  if (e.key !== 'Escape') e.stopPropagation()
}

/**
 * The dim backdrop. When `rect` is provided we draw a radial gradient that's
 * transparent at the centre of the rect (the "spotlight") and opaque outside,
 * so the highlighted element appears to glow. When `rect` is null we fall
 * back to a flat dim layer.
 */
function overlayStyle(rect: DOMRect | null): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    pointerEvents: 'auto',
  }
  if (!rect) {
    return { ...base, background: 'rgba(15, 23, 42, 0.55)' }
  }
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const rx = rect.width / 2 + 12
  const ry = rect.height / 2 + 12
  return {
    ...base,
    background: `radial-gradient(ellipse ${rx}px ${ry}px at ${cx}px ${cy}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 70%, rgba(15,23,42,0.7) 90%, rgba(15,23,42,0.7) 100%)`,
  }
}

/**
 * Tooltip positioning.
 *
 * Default: anchor below the rect, left-aligned with it. If the tooltip would
 * spill off the right edge or below the viewport, flip / clamp accordingly.
 * If there's no rect, center.
 */
function tooltipStyle(
  rect: DOMRect | null,
  placement: TourStep['placement'] = 'bottom',
): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed',
    maxWidth: TOOLTIP_MAX_WIDTH,
    width: 'min(90vw, 360px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
    zIndex: 1001,
  }
  if (!rect) {
    return {
      ...base,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    }
  }

  const vw = window.innerWidth
  const vh = window.innerHeight
  // Choose vertical position based on placement hint, with viewport fallback.
  let top: number
  if (placement === 'top') {
    top = Math.max(TOOLTIP_GUTTER, rect.top - 200 - TOOLTIP_GUTTER)
  } else {
    top = rect.bottom + TOOLTIP_GUTTER
    // If there's no room below, flip above.
    if (top + 200 > vh) top = Math.max(TOOLTIP_GUTTER, rect.top - 200 - TOOLTIP_GUTTER)
  }
  // Horizontal: prefer aligned to anchor's left, clamp to viewport.
  let left = rect.left
  if (placement === 'left')
    left = Math.max(TOOLTIP_GUTTER, rect.left - TOOLTIP_MAX_WIDTH - TOOLTIP_GUTTER)
  if (placement === 'right')
    left = Math.min(vw - TOOLTIP_MAX_WIDTH - TOOLTIP_GUTTER, rect.right + TOOLTIP_GUTTER)
  left = Math.min(Math.max(TOOLTIP_GUTTER, left), vw - TOOLTIP_MAX_WIDTH - TOOLTIP_GUTTER)

  return { ...base, top, left }
}
